import { motion } from 'framer-motion'
import { CheckCircle2, Circle, Loader2, AlertTriangle, Radio } from 'lucide-react'
import { cn } from '../lib/utils'

export interface ProgressStep {
  id: string
  label: string
  status: 'completed' | 'pending' | 'active' | 'error' | 'sent' | 'skipped'
  description?: string
}

interface ProgressTrackerProps {
  steps: ProgressStep[]
  showLabels?: boolean
  size?: 'sm' | 'md' | 'lg'
  className?: string
  onStepClick?: (step: ProgressStep, index: number) => void
}

const stepConfig: Record<
  ProgressStep['status'],
  {
    lineColor: string
    circleColor: string
    iconColor: string
    bgColor: string
    icon: React.ReactNode
    label: string
  }
> = {
  completed: {
    lineColor: 'bg-brass',
    circleColor: 'bg-brass border-brass',
    iconColor: 'text-white',
    bgColor: 'bg-brass',
    icon: <CheckCircle2 className="w-full h-full p-0.5" aria-hidden="true" />,
    label: 'Completed',
  },
  active: {
    lineColor: 'bg-gray-200',
    circleColor: 'bg-navy border-navy',
    iconColor: 'text-white',
    bgColor: 'bg-navy',
    icon: <Radio className="w-full h-full p-0.5" aria-hidden="true" />,
    label: 'In progress',
  },
  pending: {
    lineColor: 'bg-gray-200',
    circleColor: 'border-gray-300 bg-white',
    iconColor: 'text-gray-300',
    bgColor: 'bg-white',
    icon: <Circle className="w-full h-full p-0.5" aria-hidden="true" />,
    label: 'Pending',
  },
  error: {
    lineColor: 'bg-red-200',
    circleColor: 'bg-red-500 border-red-500',
    iconColor: 'text-white',
    bgColor: 'bg-red-500',
    icon: <AlertTriangle className="w-full h-full p-0.5" aria-hidden="true" />,
    label: 'Error',
  },
  sent: {
    lineColor: 'bg-yellow-300',
    circleColor: 'bg-yellow-400 border-yellow-400',
    iconColor: 'text-white',
    bgColor: 'bg-yellow-400',
    icon: <Loader2 className="w-full h-full p-0.5 animate-spin" aria-hidden="true" />,
    label: 'Sent',
  },
  skipped: {
    lineColor: 'bg-gray-200',
    circleColor: 'border-gray-300 bg-gray-100',
    iconColor: 'text-gray-400',
    bgColor: 'bg-gray-100',
    icon: <Circle className="w-full h-full p-0.5" aria-hidden="true" />,
    label: 'Skipped',
  },
}

export function ProgressTracker({
  steps,
  showLabels = true,
  size = 'md',
  className,
  onStepClick,
}: ProgressTrackerProps) {
  const completedCount = steps.filter((s) => s.status === 'completed').length
  const total = steps.length
  const percent = Math.round((completedCount / total) * 100)

  const sizeClasses = {
    sm: {
      circle: 'w-6 h-6',
      icon: 'w-4 h-4',
      line: 'h-0.5',
      text: 'text-xs',
      desc: 'text-[10px]',
      gap: 'gap-2',
    },
    md: {
      circle: 'w-9 h-9',
      icon: 'w-5 h-5',
      line: 'h-1',
      text: 'text-sm',
      desc: 'text-xs',
      gap: 'gap-4',
    },
    lg: {
      circle: 'w-12 h-12',
      icon: 'w-7 h-7',
      line: 'h-1.5',
      text: 'text-base',
      desc: 'text-sm',
      gap: 'gap-6',
    },
  }

  const s = sizeClasses[size]

  return (
    <div className={cn('w-full', className)}>
      {/* Progress summary */}
      <div className="flex items-center justify-between mb-4">
        <span className={cn('font-medium text-navy', s.text)}>
          Progress
        </span>
        <span className={cn('text-gray-500 tabular-nums', s.desc)}>
          {completedCount}/{total} ({percent}%)
        </span>
      </div>

      {/* Progress bar (background track) */}
      <div className={cn('w-full rounded-full bg-gray-200 mb-6', s.line)}>
        <motion.div
          className={cn('rounded-full bg-brass', s.line)}
          initial={{ width: 0 }}
          animate={{ width: `${percent}%` }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
        />
      </div>

      {/* Steps */}
      <div className={cn('flex items-start', s.gap)}>
        {steps.map((step, index) => {
          const config = stepConfig[step.status]
          const isLast = index === steps.length - 1
          const isClickable = !!onStepClick

          return (
            <div
              key={step.id}
              className={cn(
                'flex items-start',
                !isLast && 'flex-1',
              )}
            >
              {/* Step circle + connecting line container */}
              <div className="flex flex-col items-center">
                {/* Animated circle */}
                <motion.button
                  type="button"
                  onClick={() => onStepClick?.(step, index)}
                  disabled={!isClickable}
                  className={cn(
                    'relative flex items-center justify-center rounded-full border-2 transition-colors focus:outline-none focus:ring-2 focus:ring-brass focus:ring-offset-1',
                    config.circleColor,
                    config.bgColor,
                    s.circle,
                    isClickable && 'cursor-pointer hover:opacity-90',
                    !isClickable && 'cursor-default',
                    step.status === 'active' && 'animate-pulse',
                  )}
                  initial={false}
                  animate={{
                    scale: step.status === 'active' ? [1, 1.05, 1] : 1,
                  }}
                  transition={{
                    duration: 2,
                    repeat: step.status === 'active' ? Infinity : 0,
                    ease: 'easeInOut',
                  }}
                  aria-label={`${step.label}: ${config.label}`}
                  title={`${step.label}: ${config.label}`}
                >
                  <span className={cn('flex items-center justify-center', config.iconColor, s.icon)}>
                    {config.icon}
                  </span>

                  {/* Pulse ring for active step */}
                  {step.status === 'active' && (
                    <motion.span
                      className="absolute inset-0 rounded-full border-2 border-navy"
                      initial={{ scale: 1, opacity: 0.6 }}
                      animate={{ scale: 1.4, opacity: 0 }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                        ease: 'easeOut',
                      }}
                    />
                  )}
                </motion.button>

                {/* Label below circle */}
                {showLabels && (
                  <div className="mt-2 text-center max-w-[80px]">
                    <p
                      className={cn(
                        'font-medium leading-tight',
                        step.status === 'completed'
                          ? 'text-brass'
                          : step.status === 'active'
                            ? 'text-navy'
                            : step.status === 'error'
                              ? 'text-red-600'
                              : 'text-gray-400',
                        size === 'sm' ? 'text-[10px]' : 'text-xs',
                      )}
                    >
                      {step.label}
                    </p>
                    {step.description && size !== 'sm' && (
                      <p className={cn('text-gray-400 mt-0.5', s.desc)}>
                        {step.description}
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Connecting line to next step */}
              {!isLast && (
                <div className="flex-1 flex items-center pt-3 px-1">
                  <div className={cn('w-full rounded-full bg-gray-200 relative overflow-hidden', s.line)}>
                    {/* Animated fill */}
                    <motion.div
                      className={cn(
                        'absolute inset-y-0 left-0 rounded-full',
                        steps[index + 1]?.status === 'error'
                          ? 'bg-red-300'
                          : step.status === 'completed'
                            ? 'bg-brass'
                            : 'bg-gray-200',
                      )}
                      initial={{ width: '0%' }}
                      animate={{
                        width: step.status === 'completed' ? '100%' : '0%',
                      }}
                      transition={{ duration: 0.4, ease: 'easeOut' }}
                    />
                  </div>
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
